import { PROVINCE_BY_SLUG } from '@/app/lib/constants'

import { PROVINCE_CENTROIDS } from './provinceCentroids'

/**
 * Radio de la Tierra en km, para la fórmula de Haversine.
 */
const EARTH_RADIUS_KM = 6371

/**
 * Distancia máxima (km) al centroide más cercano para considerar la
 * coordenada "dentro" de esa provincia — más allá, se trata como fuera de
 * España / no resoluble (research.md §3, spec.md FR-012).
 */
const MAX_DISTANCE_KM = 200

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180
}

function haversineDistanceKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const dLat = toRadians(lat2 - lat1)
  const dLng = toRadians(lng2 - lng1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) ** 2
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return EARTH_RADIUS_KM * c
}

/**
 * Resuelve unas coordenadas a la provincia soportada más cercana (nearest
 * centroid, research.md §3). Devuelve `null` si la coordenada más cercana
 * supera `MAX_DISTANCE_KM` (fuera de España) o si, por cualquier motivo, el
 * slug resultante no perteneciera a `PROVINCE_BY_SLUG` (defensa en
 * profundidad — nunca debería ocurrir dado que `PROVINCE_CENTROIDS` deriva de
 * la misma fuente).
 *
 * Función pura, sin I/O: las coordenadas nunca salen de aquí (FR-014/FR-019).
 */
export function resolveProvinceFromCoordinates(latitude: number, longitude: number): string | null {
  let closestSlug: string | null = null
  let closestDistanceKm = Number.POSITIVE_INFINITY

  for (const [slug, centroid] of Object.entries(PROVINCE_CENTROIDS)) {
    const distanceKm = haversineDistanceKm(latitude, longitude, centroid.lat, centroid.lng)
    if (distanceKm < closestDistanceKm) {
      closestDistanceKm = distanceKm
      closestSlug = slug
    }
  }

  if (!closestSlug || closestDistanceKm > MAX_DISTANCE_KM) return null
  return PROVINCE_BY_SLUG.has(closestSlug) ? closestSlug : null
}
